/**
 * Refetch whatever is on screen once the offline queue actually delivers something (§8.2).
 *
 * A flush runs on app mount and on reconnect — neither triggered by anything the reader did,
 * and both usually finishing *after* the cards have already fetched and rendered. Without
 * this, queued work lands on the server correctly and then sits there invisibly: the screen
 * still shows the pre-flush state, which reads as "my task never sent" even though it did.
 * That was half of why the offline queue looked broken (DECISIONS.md D160).
 *
 * Kept in its own file rather than in `taskQueue.ts` because that module is deliberately
 * React-free — every page calls this, but nothing about the queue itself needs a hook.
 */

import { useEffect } from "react";
import { ITEM_EDITED_EVENT } from "./editPopupEvents";
import { TASK_QUEUE_DELIVERED_EVENT } from "./taskQueue";

/** Calls `onDelivered` whenever a flush pass delivers at least one queued change. Pass a
 * page's own refresh bump; it is expected to be stable (`useCallback`), as every page's
 * already is. */
export function useQueueDelivered(onDelivered: () => void): void {
  useEffect(() => {
    window.addEventListener(TASK_QUEUE_DELIVERED_EVENT, onDelivered);
    // A change made in an edit popup is the same kind of news for the cards behind it.
    window.addEventListener(ITEM_EDITED_EVENT, onDelivered);
    return () => {
      window.removeEventListener(TASK_QUEUE_DELIVERED_EVENT, onDelivered);
      window.removeEventListener(ITEM_EDITED_EVENT, onDelivered);
    };
  }, [onDelivered]);
}
